export type TutorialStepTarget = 
  | { type: 'tab'; tabName: string }
  | { type: 'button'; buttonId: string }
  | { type: 'screen'; screenName: string }
  | { type: 'element'; elementId: string }
  | { type: 'none' };

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'center';

export type TutorialStep = {
  id: string;
  title: string;
  description: string;
  target: TutorialStepTarget;
  tooltipPosition: TooltipPosition;
  /** If provided, tutorial will navigate here before trying to highlight/measure target. */
  navigateTo?: string;
  action?: () => void | Promise<void>;
  requiresInteraction?: boolean; // If true, user must interact with target before proceeding
};

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Tripvio!',
    description: "Let's take a quick tour to help you get started. We'll show you the key features in just a few steps.",
    target: { type: 'none' },
    tooltipPosition: 'center',
  },
  {
    id: 'trips-tab',
    title: 'Your Trips',
    description: 'This is your main screen where all your trips live. You can see upcoming trips, completed trips, and create new ones here.',
    target: { type: 'tab', tabName: 'index' },
    tooltipPosition: 'top',
    // NOTE: app/(tabs)/index.tsx maps to "/(tabs)" (not "/(tabs)/index").
    navigateTo: '/(tabs)',
  },
  {
    id: 'new-trip-button',
    title: 'Create Your First Trip',
    description: 'Tap the "New trip" button to start planning. You can create trips manually, use AI assistance, or import from email.',
    target: { type: 'button', buttonId: 'new-trip-button' },
    tooltipPosition: 'bottom',
    navigateTo: '/(tabs)',
  },
  {
    id: 'ai-assistant',
    title: 'Tripvio AI Assistant',
    description: 'Use our AI assistant to get personalized trip recommendations, plan your itinerary, and get travel tips tailored to your preferences.',
    target: { type: 'button', buttonId: 'ai-assistant-button' },
    tooltipPosition: 'bottom',
    navigateTo: '/(tabs)',
  },
  {
    id: 'templates',
    title: 'Trip Templates',
    description: 'Browse pre-made trip templates created by other travelers. Start from a template and customize it to fit your needs.',
    target: { type: 'element', elementId: 'templates-widget' },
    tooltipPosition: 'bottom',
    navigateTo: '/(tabs)',
  },
  {
    id: 'email-sync',
    title: 'Auto-Import Bookings',
    description: 'Connect your email to automatically import flight confirmations, hotel bookings, and reservations. No manual entry needed!',
    target: { type: 'button', buttonId: 'email-sync-button' },
    tooltipPosition: 'bottom',
    navigateTo: '/(tabs)',
  },
  {
    id: 'plan-tab',
    title: 'Plan Your Itinerary',
    description: 'The Plan tab is where you build your day-by-day itinerary. Add activities, restaurants, and manage your schedule.',
    target: { type: 'tab', tabName: 'plan' },
    tooltipPosition: 'top',
    navigateTo: '/(tabs)/plan',
  },
  {
    id: 'budget-wallet',
    title: 'Budget, Split Expenses & Nudges',
    description:
      'Use Wallet to set budgets, track spending, and split expenses with your travel group. Tripvio also sends helpful nudges when you’re trending over budget or nearing limits.',
    target: { type: 'tab', tabName: 'wallet' },
    tooltipPosition: 'top',
    navigateTo: '/(tabs)/wallet',
  },
  {
    id: 'travel-tab',
    title: 'Maps & Travel Tools',
    description: 'Access offline maps, safety features, and travel information. Perfect for when you\'re on the go.',
    target: { type: 'tab', tabName: 'travel' },
    tooltipPosition: 'top',
    // Default travel tab to Maps for the walkthrough.
    navigateTo: '/(tabs)/travel?tab=maps',
  },
  {
    id: 'more-tab',
    title: 'More Features',
    description: 'Find documents, profile settings, and additional features in the More tab. Everything else you need is here.',
    target: { type: 'tab', tabName: 'more' },
    tooltipPosition: 'top',
    navigateTo: '/(tabs)/more',
  },
  {
    id: 'get-started',
    title: "You're All Set!",
    description: 'Ready to plan your first trip? Tap "New trip" to get started, or explore the app at your own pace.',
    target: { type: 'none' },
    tooltipPosition: 'center',
  },
];

